import React, {useState} from 'react'
import {Section, TextField, CheckboxField, Label, RadioField} from 'jbc-front/components/Form'
import {toFormValues, recordDisplay} from 'utils'
import connectForm from 'reportForms/connectForm'
import {reduxForm, FormSection, formValueSelector, autofill} from 'redux-form'
import Button from 'jbc-front/components/Button'
import FormErrors, {onSubmitFail, LabelMapper} from 'jbc-front/components/FormErrors'
import {getRelatedDependents} from 'procedures/changeDependents/utils'
import {assign, find, get} from 'lodash'
import EmployeeName from 'procedures/EmployeeName'
import {ConfirmUpgradeModal} from '../components/modals/ConfirmUpgradeModal'
import {LaborConsultantFields} from 'FormFields'
import moment from 'moment'
import {useDispatch} from 'react-redux'

const formName = 'R46Report'
const selector = formValueSelector(formName)

const setElderlyBenefitCardValue = (retairedDate, targetBirthday) => {
  const ageAtRetired = moment(retairedDate).diff(targetBirthday, 'years')
  const cardValue = ageAtRetired >= 70 && ageAtRetired <= 74 ? 'need_card' : 'no_card'
  return cardValue
}

const ElderlyBenefitCardSession = () => (
  <>
    <RadioField
      name="issuance_elderly_benefit_card"
      label="高齢受給者証の交付"
      options={[
        {value: 'no_card', label: 'なし'},
        {value: 'need_card', label: 'あり'},
      ]}
    />
    <RadioField
      name="return_elderly_benefit_card"
      label="高齢受給者証の返納"
      options={[
        {value: 'no_return', label: 'なし'},
        {value: 'need_return', label: 'あり'},
      ]}
    />
  </>
)

const Form = reduxForm({
  form: formName,
  enableReinitialize: true,
  onSubmitFail,
})(({
  handleSubmit,
  submitting,
  dependentInfoValues,
  dependents,
  submitText,
  isTarget,
  employee,
  procedureStatus,
  needUpgrade,
  needLaborConsultant,
  issuanceElderlyBenefitCard,
  returnElderlyBenefitCard,
}) => {
  const [openModal, setOpenModal] = useState(false)
  const r46Dependents = getRelatedDependents(dependents, procedureStatus)
  const branchSubmit = (needUpgrade) => {
    if (needUpgrade) {
      setOpenModal(true)
    } else {
      handleSubmit()
    }
  }
  const dispatch = useDispatch()

  if (!issuanceElderlyBenefitCard) {
    const cardValue = setElderlyBenefitCardValue(employee.retired_at, employee.birthday)
    dispatch(autofill(formName, 'issuance_elderly_benefit_card', cardValue))
  }

  if (!returnElderlyBenefitCard) {
    dispatch(autofill(formName, 'return_elderly_benefit_card', 'no_return'))
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="l-title-wrap">
        <h1 className="m-title-main">健康保険被保険者証回収不能届</h1>
        <EmployeeName employee={employee} />
      </div>
      <div className="basic_information">
        <FormErrors />
        <Section>
          <Label text="回収不能の対象者選択" />
          {procedureStatus.procedure_type === 'resign' && (
            <CheckboxField name="is_target" label={recordDisplay.fullName(employee)} />
          )}
          {procedureStatus.procedure_type === 'resign' && <ElderlyBenefitCardSession />}
          {procedureStatus.procedure_type === 'resign' && isTarget && (
            <TextField name="reason" label="被保険者証を返納できない理由" />
          )}
          <LabelMapper name="r46_dependent_param_set" label="家族情報" />
          {r46Dependents &&
            r46Dependents.map((dependent, index) => {
              const value = (dependentInfoValues && dependentInfoValues[index]) || {}
              return (
                <FormSection name={`r46_dependent_param_set[${index}]`} key={dependent.id}>
                  <CheckboxField name="is_target" label={recordDisplay.fullName(dependent)} />
                  <ElderlyBenefitCardSession />
                  {value.is_target && <TextField name="reason" label="被保険者証を返納できない理由" />}
                </FormSection>
              )
            })}
          {procedureStatus.procedure_type === 'resign' && <TextField name="notes" label="備考" />}
          <LaborConsultantFields
            needLaborConsultant={needLaborConsultant}
            procedureStatusId={procedureStatus.id}
            reportName={'R46'}
          />
          <div className="u-ta-c u-mt30">
            <Button primary onClick={() => branchSubmit(needUpgrade)} disabled={submitting}>
              {submitText}
            </Button>
          </div>
        </Section>
      </div>
      <ConfirmUpgradeModal
        onSubmit={() => {
          setOpenModal(false)
          handleSubmit()
        }}
        closeModal={() => setOpenModal(false)}
        openModal={openModal}
      />
    </form>
  )
})
const makeInitialValues = ({dependents, data: reportValues, procedureStatus}) => {
  if (!dependents) {
    return toFormValues(reportValues)
  }
  const r46DependentParamSet = get(reportValues, 'r46_dependent_param_set') || []
  const r46Dependents = getRelatedDependents(dependents, procedureStatus)
  const r46DependentValues = r46Dependents.map((dependent) =>
    toFormValues(
      find(r46DependentParamSet, (value) => value.employee_dependent_id == dependent.id) || {
        employee_dependent_id: dependent.id,
      }
    )
  )

  const retairedDate = procedureStatus.employee.retired_at
  const birthdayList = r46Dependents.map((dependent) => dependent.birthday)
  r46DependentValues.forEach((dependentValue, index) => {
    if (!dependentValue.issuance_elderly_benefit_card) {
      const cardValue = setElderlyBenefitCardValue(retairedDate, birthdayList[index])
      dependentValue.issuance_elderly_benefit_card = cardValue
    }
    if (!dependentValue.return_elderly_benefit_card) {
      dependentValue.return_elderly_benefit_card = 'no_return'
    }
  })

  return assign(toFormValues(reportValues), {r46_dependent_param_set: r46DependentValues})
}
const connectedForm = connectForm('r46', Form, {
  mapState: (state) => ({
    dependents: get(state, 'procedureStatuses.current.data.employee.employee_dependents'),
    employee: get(state, 'procedureStatuses.current.data.employee') || {},
    dependentInfoValues: selector(state, 'r46_dependent_param_set'),
    isTarget: selector(state, 'is_target'),
    procedureStatus: state.procedureStatuses.current.data,
    needUpgrade: selector(state, 'need_upgrade'),
    needLaborConsultant: selector(state, 'need_labor_consultant'),
    issuanceElderlyBenefitCard: selector(state, 'issuance_elderly_benefit_card'),
    returnElderlyBenefitCard: selector(state, 'return_elderly_benefit_card'),
  }),
  makeInitialValues,
})

export default connectedForm
